import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articleMap, articles, getLatestArticles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleMap[slug] : undefined;

  if (!article) {
    return (
      <>
        <SEOHead
          title="文章未找到 - 蘑菇TV入口"
          description="抱歉，您查找的文章不存在。请返回首页浏览其他内容。"
          keywords={siteInfo.keywords}
          canonical={`${siteInfo.url}/article/${slug}`}
        />
        <Navbar />
        <main className="pt-24 pb-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-primary-100">
              <i className="ri-error-warning-line text-3xl text-primary-500"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-900 mb-3 font-heading">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您查找的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-md text-sm font-medium transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  if (relatedArticles.length < 3) {
    const extra = getLatestArticles(3 - relatedArticles.length).filter(
      (a) => a.slug !== article.slug && !relatedArticles.find((r) => r.slug === a.slug)
    );
    relatedArticles.push(...extra);
  }

  const pageTitle = `${article.title} - ${siteInfo.name}`;
  const pageDescription = article.excerpt;
  const pageUrl = `${siteInfo.url}/article/${slug}`;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    dateModified: article.updateDate,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl,
    },
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={article.tags.join(',')}
        canonical={pageUrl}
        ogTitle={pageTitle}
        ogDescription={pageDescription}
        ogImage={article.imageUrl}
        ogType="article"
        publishedTime={article.publishDate}
        modifiedTime={article.updateDate}
      />
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20 pb-14 px-4 md:px-6">
        <article className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="text-xs font-medium px-3 py-1 rounded-full bg-accent-100 text-accent-800 hover:bg-accent-200 transition-colors whitespace-nowrap"
              >
                {article.categoryName}
              </Link>
              <span className="text-sm text-foreground-400">{article.publishDate} 发布</span>
              <span className="text-sm text-foreground-400">{article.updateDate} 更新</span>
              <span className="text-sm text-foreground-400">{article.readTime} 分钟阅读</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4 font-heading leading-snug">
              {article.title}
            </h1>
            <p className="text-foreground-500 leading-relaxed text-base">{article.excerpt}</p>
          </header>

          <div className="mb-8 rounded-lg overflow-hidden aspect-[16/9]">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
              [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:font-heading
              [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground-800 [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:font-heading
              [&_p]:mb-4 [&_p]:leading-relaxed
              [&_strong]:text-foreground-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-400 mr-1">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-10 border-t border-background-200/70">
              <h2 className="text-xl font-bold text-foreground-900 mb-6 font-heading">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group block bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 font-heading">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-2">{ra.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}